import { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/apiClient';

/**
 * Комментарии записи.
 *
 * @param {number} idPost - глобальный ID записи
 * @param {'newest'|'oldest'|'likes'} [sort] - порядок; сервер знает те же три
 * @param {string|null} [linkHash] - неугадываемый адрес записи. Комментарии
 *   закрыты вместе с записью, и доступ «по ссылке» открывает только он: сквозной
 *   id перебирается (см. server/services/viewerContext.js).
 */
export function useCommentsPost(idPost, sort = 'newest', linkHash = null) {
    const [comments, setComments] = useState([]);
    const [restriction, setRestriction] = useState({ blocked: false });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!idPost) return;

        const controller = new AbortController();

        const fetchComments = async () => {
            try {
                setLoading(true);
                setError(null);

                const url = new URL('/api/post/comments', process.env.REACT_APP_API_URL);
                url.searchParams.set('id', idPost);
                url.searchParams.set('sort', sort);
                if (linkHash) url.searchParams.set('linkHash', linkHash);

                const accessToken = localStorage.getItem('accessToken');

                const response = await apiFetch(url.toString(), {
                    method: 'GET',
                    credentials: 'include',
                    signal: controller.signal,
                    headers: accessToken
                        ? { Authorization: `Bearer ${accessToken}` }
                        : undefined,
                });

                if (!response.ok) {
                    throw new Error('Ошибка при получении комментариев');
                }

                const data = await response.json();
                setComments(data?.comments ?? []);
                setRestriction(data?.restriction ?? { blocked: false });
            } catch (err) {
                if (err.name === 'AbortError') return;

                setError(
                    err instanceof Error
                        ? err.message
                        : 'Неизвестная ошибка'
                );
            } finally {
                setLoading(false);
            }
        };

        fetchComments();

        return () => {
            controller.abort();
        };
    }, [idPost, sort, linkHash]);

    return { comments, restriction, loading, error };
}
